/**
 * ViewerProviders — the workspace service chain every viewer composition mounts.
 *
 * One shared compile over the store's files (the same context the workbench
 * primitives read, so they mount inside the viewer as-is), plus the playback /
 * placements / layout / compare services. Both the current `App` shell and the
 * new `ViewerComposition` wrap their content in this, so the provider order +
 * options live in exactly one place.
 *
 *   WorkspaceProvider          — files + selection + view state (the store)
 *   WorkspaceModelProvider     — the shared compiled model (no active-file
 *                                following; no debounce — bulk pushes)
 *   ProcessPlaybackProvider    — scrubber / walker / swimlane
 *   PlacementsProvider         — hand-placement overrides + undo/redo
 *   LayoutProvider             — the board solve
 *   CompareProvider            — diff target + swap
 *   BoardHandleProvider        — the board handle + board↔nav hover channel
 */
import type { ComponentProps, ReactNode, ReactElement } from "react";
import { WorkspaceProvider } from "./WorkspaceProvider.js";
import { QuietDiagnosticsProvider } from "./QuietDiagnosticsProvider.js";
import { EditModeProvider } from "./EditModeProvider.js";
import { WorkspaceModelProvider } from "./useWorkspaceModel.js";
import { ActiveViewProvider } from "./ActiveViewProvider.js";
import { ProcessPlaybackProvider } from "./ProcessPlaybackProvider.js";
import { PlacementsProvider } from "./PlacementsProvider.js";
import { LayoutProvider } from "./LayoutProvider.js";
import { CompareProvider } from "./CompareProvider.js";
import { BoardHandleProvider } from "./BoardHandleProvider.js";
import { HotkeyRegistryProvider } from "./plugins/hotkeys.js";
import { CommandSourceProvider } from "./plugins/commandSources.js";
import type { WorkspaceStore } from "./WorkspaceStore.js";
import type { FileSource } from "./filesource/types.js";

type StoreOptions = ComponentProps<typeof WorkspaceProvider>["storeOptions"];
type CompareAgainst = ComponentProps<typeof CompareProvider>["initialAgainst"];

export interface ViewerProvidersProps {
  readonly source: FileSource;
  readonly storeOptions?: StoreOptions;
  /** Persist working edits to localStorage under this key (the editor's
   *  scratch overlay survives reload). Omit for no edit-persistence. */
  readonly overlayKey?: string;
  /** Use a caller-built, pre-seeded store instead of seeding from `source`.
   *  The Studio host hands its changeset store here (base = committed trunk,
   *  working = the draft); `source` then drives binders only. */
  readonly store?: WorkspaceStore;
  readonly initialAgainst?: CompareAgainst;
  readonly initialSwapped?: boolean;
  readonly children: ReactNode;
}

export function ViewerProviders({
  source,
  storeOptions,
  overlayKey,
  store,
  initialAgainst = null,
  initialSwapped = false,
  children,
}: ViewerProvidersProps): ReactElement {
  return (
    <WorkspaceProvider
      source={source}
      store={store}
      storeOptions={storeOptions}
      overlayKey={overlayKey}
    >
      <QuietDiagnosticsProvider>
      <EditModeProvider>
      <WorkspaceModelProvider followActiveFile={false} debounceMs={0}>
        <ActiveViewProvider>
        <ProcessPlaybackProvider>
          <PlacementsProvider>
            <LayoutProvider>
              <CompareProvider
                initialAgainst={initialAgainst}
                initialSwapped={initialSwapped}
              >
                <BoardHandleProvider>
                  <HotkeyRegistryProvider>
                    <CommandSourceProvider>{children}</CommandSourceProvider>
                  </HotkeyRegistryProvider>
                </BoardHandleProvider>
              </CompareProvider>
            </LayoutProvider>
          </PlacementsProvider>
        </ProcessPlaybackProvider>
        </ActiveViewProvider>
      </WorkspaceModelProvider>
      </EditModeProvider>
      </QuietDiagnosticsProvider>
    </WorkspaceProvider>
  );
}
